import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PhotoIcon,
  CubeIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  PauseIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { fetchListingDetail, publishListing, pauseListing, resumeListing, deleteListing } from '../api/marketplaceApi';
import { API_URL } from '../../../api/config';
import ConfirmCommandDialog from '../components/ConfirmCommandDialog';
import type { Listing, ListingDeleteBlocked } from '../types';

const statusStyles: Record<string, { bg: string; text: string; border: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },
  active: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  paused: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  expired: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  sold_out: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  deleted: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
};

const SupplierListingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState<ListingDeleteBlocked | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchListingDetail(Number(id))
      .then(res => setListing(res.item || res.listing || res))
      .catch(err => setError(err.message || 'Failed to load listing'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePublish = async () => {
    if (!listing) return;
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      await publishListing(listing.id);
      setListing({ ...listing, status: 'active' });
      setInfo('Listing published.');
    } catch (err: any) {
      setError(err.message || 'Failed to publish');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    if (!listing) return;
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      await pauseListing(listing.id);
      setListing({ ...listing, status: 'paused' });
      setInfo('Listing paused.');
    } catch (err: any) {
      setError(err.message || 'Failed to pause');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!listing) return;
    setActionLoading(true);
    setError(null);
    setInfo(null);
    try {
      await resumeListing(listing.id);
      setListing({ ...listing, status: 'active' });
      setInfo('Listing resumed and is live again.');
    } catch (err: any) {
      setError(err.message || 'Failed to resume listing');
    } finally {
      setActionLoading(false);
    }
  };

  const openDelete = () => {
    setDeleteBlocked(null);
    setError(null);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!listing) return;
    setActionLoading(true);
    setError(null);
    setInfo(null);
    setDeleteBlocked(null);
    try {
      await deleteListing(listing.id);
      setConfirmOpen(false);
      setInfo('Listing deleted. It is no longer visible in the public marketplace.');
      setTimeout(() => navigate('/marketplace/supplier/listings'), 1000);
    } catch (err: any) {
      setConfirmOpen(false);
      if (err?.code === 'LISTING_DELETE_BLOCKED') {
        setDeleteBlocked(err as ListingDeleteBlocked);
      } else {
        setError(err?.message || 'Failed to delete listing');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const resolveImg = (url?: string) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const s = statusStyles[listing?.status || 'draft'] || statusStyles.draft;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
          <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error || 'Listing not found'}</p>
        </div>
      </div>
    );
  }

  const img = resolveImg(listing.image_url);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors">
        <ArrowLeftIcon className="w-4 h-4" /> Back to listings
      </button>

      {/* Header Card */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <CubeIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">{listing.title}</h1>
              <p className="text-emerald-100 mt-1">{listing.catalog_name || 'Listing'} · {listing.supplier_name || 'Your Store'}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold border ${s.bg} ${s.text} ${s.border}`}>
            {listing.status}
          </span>
        </div>
      </div>

      {/* Info message */}
      {info && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
          <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
          <p className="text-sm text-emerald-800 font-medium">{info}</p>
        </div>
      )}

      {/* Delete blocked */}
      {deleteBlocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-amber-800">Cannot delete this listing</p>
            <p className="text-sm text-amber-700 mt-1">{deleteBlocked.error}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {deleteBlocked.reasons.map((r, i) => (
                <span key={i} className="px-2.5 py-1 bg-white border border-amber-300 text-amber-800 rounded-full text-xs font-semibold">{r}</span>
              ))}
            </div>
            <button
              onClick={() => setDeleteBlocked(null)}
              className="mt-4 flex items-center gap-1.5 text-sm font-bold text-amber-800 hover:text-amber-900"
            >
              <XMarkIcon className="w-4 h-4" /> Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 flex-1 flex-wrap">
          {listing.status === 'draft' && (
            <button
              onClick={handlePublish}
              disabled={actionLoading}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-bold text-sm disabled:opacity-50 transition-all"
            >
              <PlayIcon className="w-5 h-5" />
              {actionLoading ? 'Publishing...' : 'Publish Listing'}
            </button>
          )}
          {listing.status === 'active' && (
            <button
              onClick={handlePause}
              disabled={actionLoading}
              className="flex items-center gap-2 px-6 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 font-bold text-sm disabled:opacity-50 transition-all"
            >
              <PauseIcon className="w-5 h-5" />
              {actionLoading ? 'Pausing...' : 'Pause Listing'}
            </button>
          )}
          {listing.status === 'paused' && (
            <button
              onClick={handleResume}
              disabled={actionLoading}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-bold text-sm disabled:opacity-50 transition-all"
            >
              <ArrowPathIcon className="w-5 h-5" />
              {actionLoading ? 'Resuming...' : 'Resume Listing'}
            </button>
          )}
          <button
            onClick={() => navigate(`/marketplace/supplier/listings/${listing.id}/edit`)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-bold text-sm transition-all"
          >
            <PencilSquareIcon className="w-5 h-5" />
            Edit
          </button>
          <button
            onClick={openDelete}
            disabled={actionLoading || listing.status === 'active'}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 font-bold text-sm disabled:opacity-50 transition-all"
          >
            <TrashIcon className="w-5 h-5" />
            Delete
          </button>
        </div>
        {listing.status === 'active' && (
          <p className="text-xs text-gray-400">Pause this listing before deleting it.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Image */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {img ? (
            <img src={img} alt={listing.title} className="w-full h-72 object-cover" />
          ) : (
            <div className="h-72 bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
              <PhotoIcon className="w-20 h-20 text-emerald-200" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            {listing.grade && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 border border-gray-200 rounded-full text-xs font-bold">
                Grade: {listing.grade}
              </span>
            )}
            {listing.certification && (
              <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
                {listing.certification}
              </span>
            )}
            {listing.traceability_enabled && (
              <span className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-bold">
                Traceable
              </span>
            )}
          </div>

          {/* Price */}
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="w-5 h-5 text-emerald-600" />
              <span className="text-3xl font-extrabold text-emerald-700">{listing.currency} {Number(listing.price_per_unit).toLocaleString()}</span>
            </div>
            <p className="text-sm text-emerald-600 mt-1">per {listing.commercial_unit}</p>
          </div>

          {/* Specs Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Unit</p>
              <p className="text-sm font-bold text-gray-700">{listing.commercial_unit}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Min. Order</p>
              <p className="text-sm font-bold text-gray-700">{listing.minimum_order_quantity} {listing.commercial_unit}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Available</p>
              <p className="text-sm font-bold text-gray-700">{listing.available_quantity} {listing.commercial_unit}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Reserved</p>
              <p className="text-sm font-bold text-gray-700">{listing.reservation_quantity || 0} {listing.commercial_unit}</p>
            </div>
          </div>

          {/* Location */}
          {listing.fulfillment_city && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPinIcon className="w-4 h-4 text-gray-400" />
              <span>{listing.fulfillment_location}{listing.fulfillment_location && listing.fulfillment_city ? ', ' : ''}{listing.fulfillment_city}</span>
            </div>
          )}

          {/* Dates */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <ClockIcon className="w-3.5 h-3.5" />
              <span>Created: {formatDate(listing.created_at)}</span>
            </div>
            {listing.published_at && (
              <div className="flex items-center gap-2 text-xs text-emerald-600">
                <CheckCircleIcon className="w-3.5 h-3.5" />
                <span className="font-medium">Published: {formatDate(listing.published_at)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {listing.description && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-2">Description</h3>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{listing.description}</p>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmCommandDialog
        open={confirmOpen}
        title="Delete this listing?"
        description={`"${listing.title}" will disappear from the public marketplace and your listings. This action is permanent but keeps order and sales history intact.`}
        confirmLabel="Delete Listing"
        cancelLabel="Cancel"
        variant="danger"
        loading={actionLoading}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default SupplierListingDetail;
